<template>
    <div>
        <!-- Header Section -->
        <div class="content-header">
            <div class="container-fluid">
                <div class="row mb-2">
                    <div class="col-sm-6">
                        <h1 class="m-0">{{ this.$route.name }}</h1>
                    </div>
                    <div class="col-sm-6">
                        <ol class="breadcrumb float-sm-right">
                            <li class="breadcrumb-item active">
                                <i :class="routeIcon"></i>
                                {{ $t(this.$route.name) }}
                            </li>
                        </ol>
                    </div>
                </div>
            </div>
        </div>

        <!-- Main Content Section -->
        <section class="content">
            <div class="container-fluid">
                <!-- Filters Section -->
                <b-row class="col-lg-12">
                    <div class="col-lg-3 col-md-3 col-sm-12">
                        <b-form-group label-cols-lg="3" label="Operators" label-for="filter-partners">
                            <multiselect id="filter-partners" v-model="filters.partners" :options="operatorOptions"
                                label="text" track-by="value" :multiple="true" :placeholder="'Select Operators'"
                                :internal-search="true" />
                        </b-form-group>
                    </div>

                    <div class="col-lg-3 col-md-3 col-sm-12">
                        <b-form-group label-cols-lg="3" label="Table" label-for="filter-tableId">
                            <multiselect id="filter-tableId" v-model="filters.tableIds" :options="tableOptions"
                                label="label" track-by="value" :multiple="true" :placeholder="'Select Tables'"
                                :internal-search="true" :disabled="!tableOptionsLoaded"
                                :loading="!tableOptionsLoaded" />
                        </b-form-group>
                    </div>

                    <div v-for="({ id, label }) in filterInputs" :key="id" class="col-lg-3 col-md-3 col-sm-12">
                        <b-form-group label-cols-lg="3" :label-for="`${id}-filter-input`" :label="label">
                            <b-form-input :id="`${id}-filter-input`" v-model="filters[id]"
                                :placeholder="`Enter ${label}`" autocomplete="off" />
                        </b-form-group>
                    </div>

                    <div class="col-lg-3 col-md-3 col-sm-12">
                        <b-form-group label-cols-lg="3" label="Start Date" label-for="start-date-input">
                            <date-picker id="start-date-input" v-model="filters.startDate" type="datetime"
                                format="YYYY-MM-DD HH:mm:ss" value-type="YYYY-MM-DD HH:mm:ss"
                                placeholder="Select start date and time" :lang="datePickerLang"></date-picker>
                        </b-form-group>
                    </div>

                    <div class="col-lg-3 col-md-3 col-sm-12">
                        <b-form-group label-cols-lg="3" label="End Date" label-for="end-date-input">
                            <date-picker id="end-date-input" v-model="filters.endDate" type="datetime"
                                format="YYYY-MM-DD HH:mm:ss" value-type="YYYY-MM-DD HH:mm:ss"
                                placeholder="Select end date and time" :lang="datePickerLang"></date-picker>
                        </b-form-group>
                    </div>

                    <div class="col-lg-3 col-md-3 col-sm-12">
                        <b-form-group>
                            <b-form-checkbox v-model="filters.hideSystemMessages" switch>
                                Hide System Messages
                            </b-form-checkbox>
                        </b-form-group>
                    </div>

                    <!-- Block/Unblock Section -->
                    <div v-if="searchMode && !isDisabledBlock" class="col-lg-6 col-md-6 col-sm-12">
                        Block/Unblock:
                        <button v-for="uni in filterUnique" :key="uni.id" class="btn" :class="{
                            'btn-primary': !uni.chatBlock,
                            'btn-danger': uni.chatBlock
                        }" style="margin-right: 5px;" @click="handleBlock(uni)">
                            {{ uni.playerId }} ({{ uni.partnerName }})
                        </button>
                        <b v-if="filterUnique.length === 0">Users not found</b>
                    </div>
                </b-row>

                <!-- Filter Buttons -->
                <b-row class="col-lg-12">
                    <div class="col-lg-12 col-md-12 col-sm-12 apply-clear">
                        <FilterButtons :cancel="cancel" :load-fn="loadItems" :cancel-fn="cancelRequest"
                            :reset-fn="clearFilters" />
                    </div>
                </b-row>

                <b-row class="col-lg-12 mb-3">
                    <div class="col-lg-12 col-md-12 col-sm-12">
                        <b-button variant="success" @click="exportToCSV" :disabled="items.length === 0" class="mr-2">
                            <i class="fas fa-file-csv"></i> Export to CSV ({{ items.length }} records)
                        </b-button>
                    </div>
                </b-row>

                <!-- Table Section -->
                <div class="row col-12" id="loading-container">
                    <div class="col-12 mb-10">
                        <div class="mb-10">
                            <div class="scroll-wrapper">
                                <div class="scroll"></div>
                            </div>
                            <div class="table-wrapper mb-10">
                                <b-table :items="formattedItems" :fields="fields" :striped="true" :small="true"
                                    :hover="true" :busy="!!cancel" :sort-by="sortBy" :sort-desc="sortDesc"
                                    :empty-text="emptyText" :no-local-sorting="true" :sort-icon-left="true" show-empty
                                    @sort-changed="sortingChanged">

                                </b-table>
                            </div>
                        </div>

                        <!-- Pagination Section -->
                        <div class="row mb-10">
                            <div class="col-12">
                                <b-pagination v-model="pagination.page" :total-rows="pagination.records"
                                    :per-page="pagination.onPage" class="float-right" first-number last-number
                                    @change="onPageChange" />
                            </div>
                        </div>

                        <div class="row mb-10">
                            <div class="col-12">
                                <ve-pagination :page-index="pagination.page" :page-size="pagination.onPage"
                                    :total="pagination.records" :paging-count="pagination.count"
                                    :page-size-option="pagination.pageSizeOption" :layout="['total', 'sizer']"
                                    class="float-right" @on-page-size-change="onPageSizeChange" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    </div>
</template>

<script>
import EditSourceForm from "@/components/Sources/EditSourceForm";
import RemoveForm from "@/components/remove-form/RemoveForm.vue";
import FilterButtons from "@/components/FilterButtons";
import axios from "axios";
import { getRouteIcon, ROUTE_SOURCES } from "@/utils/routerIcons";
import Multiselect from "vue-multiselect";
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';

const DEFAULT_PAGINATION = {
    onPage: 100,
    page: 1,
    records: 0,
    count: 5,
    pageSizeOption: [100, 200, 500, 1000],
};

export default {
    name: 'ChatHistory',

    components: {
        EditForm: EditSourceForm,
        RemoveForm,
        FilterButtons,
        Multiselect,
        DatePicker
    },

    data() {
        return {
            cancel: null,
            error: null,
            fields: [
                { key: 'id', label: '_ID', sortable: false },
                { key: 'message', label: 'Message', sortable: false },
                { key: 'partnerName', label: 'Operator Name', sortable: false },
                { key: 'dealerName', label: 'Dealer Name', sortable: false },
                { key: 'playerName', label: 'Player Name', sortable: false },
                { key: 'chatBlock', label: 'Blocked', sortable: false },
                { key: 'playerId', label: 'PlayerId', sortable: false },
                { key: 'tableId', label: 'TableId', sortable: false },
                { key: 'date', label: 'Date', sortable: true }
            ],
            items: [],
            sortBy: 'date',
            sortDesc: true,
            filters: {
                partners: [],
                tableIds: [],
                startDate: '',
                endDate: '',
                startTime: '',
                endTime: '',
                hideSystemMessages: false
            },
            pagination: { ...DEFAULT_PAGINATION },
            filterInputs: [
                // { label: 'Partner', id: 'partner' },
                { label: 'PlayerId', id: 'playerId' },
                { label: 'Player Name', id: 'playerName' },
                { label: 'Dealer Name', id: 'dealerName' }
            ],
            isDisabledBlock: false,
            searchMode: false,
            tableOptions: [],
            tableOptionsLoaded: false,
            datePickerLang: {
                days: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
                months: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
                placeholder: {
                    date: 'Select Date',
                    dateRange: 'Select Date Range'
                },
                title: {
                    date: 'Select Date',
                    dateRange: 'Select Date Range',
                    time: 'Select Time',
                    year: 'Select Year',
                    month: 'Select Month',
                    day: 'Select Day'
                }
            }
        }
    },

    computed: {
        operatorOptions() {
            return (this.$store.getters['limits/operatorsForSelect']?.map(option => {
                return { value: option.label, text: option.label };
            }) || []).sort((a, b) => a.text.localeCompare(b.text));
        },
        filterUnique() {
            return this.items.filter((value, index, self) =>
                self.findIndex(v => v.uid === value.uid && v.partnerId === value.partnerId) === index
            );
        },

        emptyText() {
            if (this.cancel) return 'Loading items..';
            if (this.error) return this.error;
            return 'There are no records to show';
        },

        routeIcon: () => getRouteIcon(ROUTE_SOURCES),

        tablesFromStore() {
            return this.$store.getters['tablesForSelect/list'] || [];
        },

        formattedItems() {
            return this.items.map(item => {
                const formattedItem = { ...item };

                if (formattedItem.playerName === 'system') {
                    formattedItem.playerName = '';
                }

                return formattedItem;
            });
        }
    },

    watch: {
        tablesFromStore: {
            handler(newTables) {
                if (newTables && newTables.length > 0) {
                    this.tableOptions = newTables.map(table => {
                        return {
                            label: `${table.value} - ${table.label}`,
                            value: table.value
                        };
                    });
                    this.tableOptionsLoaded = true;
                }
            },
            immediate: true
        }
    },

    methods: {
        parseQueryParams() {
            const query = this.$route.query;

            if (query.playerId) {
                this.filters.playerId = query.playerId;
            }

            if (query.partner_id) {
                const partner = this.operatorOptions.find(op => op.value === query.partner_id);
                if (partner) {
                    this.filters.partners = [partner];
                }
            }
        },

        async handleBlock(uni) {
            try {
                await this.$http({
                    url: '/api/v1/chat-history/block-user',
                    method: 'POST',
                    data: {
                        playerId: uni.playerId,
                        partnerId: uni.partnerId,
                        key: process.env.VUE_APP_CHAT_API_KEY
                    }
                });

                this.loadItems();
            } catch (error) {
                console.error(error);
            }
        },

        sortingChanged(ctx) {
            this.sortBy = ctx.sortBy;
            this.sortDesc = ctx.sortDesc;
            this.pagination.page = 1;
            this.loadItems();
        },

        handleItemsUpdate() {
            this.loadItems();
        },

        clearFilters() {
            this.searchMode = false;
            this.filters = {
                partners: [],
                tableIds: [],
                startDate: '',
                endDate: '',
                startTime: '',
                endTime: '',
                hideSystemMessages: false
            };
            this.sortingChanged({
                sortBy: 'date',
                sortDesc: true
            });
        },

        async loadItems() {
            const loadingInstance = this.$veLoading({
                target: document.querySelector("#loading-container"),
                name: "wave"
            });

            this.items = [];
            loadingInstance.show();

            try {
                const params = {
                    ...this.filters,
                    limit: parseInt(this.pagination.onPage),
                    offset: parseInt(this.pagination.onPage) * (this.pagination.page - 1),
                    orderBy: this.sortBy,
                    direction: this.sortDesc ? 'DESC' : 'ASC'
                };

                if (this.filters.tableIds && this.filters.tableIds.length > 0) {
                    params.tableIds = this.filters.tableIds.map(table => table.value);
                }
                
                if (this.filters.partners && this.filters.partners.length > 0) {
                    params.partners = this.filters.partners.map(partner => partner.value);
                }

                const response = await this.$http({
                    url: '/api/v1/chat-history',
                    params: params,
                    method: 'GET',
                    cancelToken: new axios.CancelToken(c => { this.cancel = c })
                });

                const { items, pagination: { limit, totalCount } } = response.data;
                this.items = items;
                this.pagination.onPage = limit;
                this.pagination.records = totalCount;
                this.searchMode = !!this.filters.playerId;
                this.error = '';
            } catch (error) {
                this.items = [];
                this.error = error;
                this.searchMode = false;
            } finally {
                loadingInstance.close();
                this.cancel = null;
            }
        },

        cancelRequest() {
            if (this.cancel) {
                this.cancel('Cancelling previous request');
            }
        },

        onPageChange(page) {
            this.pagination.page = page;
            this.loadItems();
        },

        onPageSizeChange(pageSize) {
            this.pagination.page = 1;
            this.pagination.onPage = parseInt(pageSize);
            this.loadItems();
        },

        loadTableOptions() {
            this.$store.dispatch('tablesForSelect/loadList');
        },

        exportToCSV() {
            if (this.items.length === 0) {
                this.$toastr.e("No data to export");
                return;
            }

            const headers = this.fields.map(field => field.label);

            const csvRows = [];

            csvRows.push(headers.join(';'));

            this.items.forEach(item => {
                const values = this.fields.map(field => {
                    const value = item[field.key];

                    if (value === null || value === undefined) {
                        return '';
                    }

                    const stringValue = String(value);
                    if (stringValue.includes(';') || stringValue.includes('"')) {
                        return `"${stringValue.replace(/"/g, '""')}"`;
                    }
                    return stringValue;
                });

                csvRows.push(values.join(';'));
            });

            const csvContent = csvRows.join('\n');

            const BOM = "\uFEFF";
            const csvContentWithBOM = BOM + csvContent;

            const blob = new Blob([csvContentWithBOM], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.setAttribute('href', url);

            const now = new Date();
            const dateStr = now.toISOString().slice(0, 10);
            const timeStr = now.toTimeString().slice(0, 8).replace(/:/g, '-');

            let fileName = `chat_history_${dateStr}_${timeStr}`;
            
            if (this.filters.tableIds && this.filters.tableIds.length > 0) {
                fileName += `_tables_${this.filters.tableIds.map(t => t.value).join('-')}`;
            }
            
            if (this.filters.partners && this.filters.partners.length > 0) {
                fileName += `_operators_${this.filters.partners.map(p => p.value).join('-')}`;
            }

            if (this.filters.startDate) {
                const startDate = this.filters.startDate.split(' ')[0].replace(/[-:]/g, '');
                fileName += `_from_${startDate}`;
            }

            if (this.filters.endDate) {
                const endDate = this.filters.endDate.split(' ')[0].replace(/[-:]/g, '');
                fileName += `_to_${endDate}`;
            }

            link.setAttribute('download', `${fileName}.csv`);
            document.body.appendChild(link);

            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

            this.$toastr.s(`Exported ${this.items.length} records to CSV`);
        }
    },

    created() {
        this.loadTableOptions();
    },

    mounted() {
        this.$store.dispatch('limits/ensureLoadOperatorsForSelect');
        this.parseQueryParams();
        this.loadItems();
    }
}
</script>

<style>
.mx-datepicker {
    width: 100%;
}
</style>
